import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import { ActionIcon } from "PFComponents/action_icon";
import { Alert } from "PFComponents/alert";
import { Typography } from "PFComponents/typography";
import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { BOOKING_PAGE_HEADER_HEIGHT } from "../../../booking_page";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import css from "./split_mode_warning_overlay.module.scss";

type SplitModeWarningOverlayProps = {
  headerRef: React.MutableRefObject<HTMLDivElement>;
};

const SPLIT_MODE_ALERT_TOP = 46;
const TOP_NAVBAR_HEIGHT = 30;
const TOOLBAR_TOP_DISTANCE = 6;

const checkIfShouldBeSticky = () =>
  window.scrollY >=
  BOOKING_PAGE_HEADER_HEIGHT + TOP_NAVBAR_HEIGHT + TOOLBAR_TOP_DISTANCE - SPLIT_MODE_ALERT_TOP;

export const SplitModeWarningOverlay = ({ headerRef }: SplitModeWarningOverlayProps) => {
  const { t } = useTranslation("bookingModule", { keyPrefix: "splitMode.warningOverlay" });
  const ref = useRef<HTMLDivElement>(null);

  const { setSplitMode } = useBookingsOverviewContext();
  const { setHeaderDisabled } = useBookingModuleContext();

  const exitSplitMode = () => {
    setSplitMode(false);
    setHeaderDisabled(false);
  };

  const scrollListener = useCallback(() => {
    if (!ref.current) {
      return;
    }
    const isSticky = checkIfShouldBeSticky();
    if (isSticky) {
      const distanceFromViewportTopToStickyPlace =
        BOOKING_PAGE_HEADER_HEIGHT + TOP_NAVBAR_HEIGHT + TOOLBAR_TOP_DISTANCE - window.scrollY;
      ref.current.style.top = `${Math.max(TOOLBAR_TOP_DISTANCE, distanceFromViewportTopToStickyPlace)}px`;
    } else {
      ref.current.style.top = `${SPLIT_MODE_ALERT_TOP}px`;
    }
  }, []);

  useEffect(() => {
    scrollListener();
    document.addEventListener("scroll", scrollListener);
    return () => {
      document.removeEventListener("scroll", scrollListener);
    };
  }, [scrollListener]);

  if (!headerRef.current) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div className={css.container}>
          <div className={css.content} ref={ref}>
            <Alert
              small
              hideIcon
              kind="info"
              title={
                <span className={css.title}>
                  <ActionIcon name="cross" onClick={exitSplitMode} disableHover />
                  <Typography variant="bodyRegular" noMargin>
                    {t("message")}
                  </Typography>
                </span>
              }
            />
          </div>
        </div>,
        headerRef.current
      )}
    </>
  );
};
